<!--
  Transmission Table Component
  Displays transmission records with retry and cancel actions
  @component TransmissionTable
-->
<template>
  <div class="transmission-table">
    <q-table
      :rows="transmissions"
      :columns="columns"
      :loading="loading"
      row-key="id"
      flat
      bordered
      dense>
      <!-- Loading slot -->
      <template #loading>
        <q-inner-loading
          showing
          color="primary" />
      </template>

      <!-- Top slot -->
      <template #top>
        <div class="row items-center justify-between full-width">
          <div class="col-auto">
            <h6 class="q-ma-none">{{ t('composites.TransactionProcessingDashboard.transmissions.title') }}</h6>
            <p class="text-caption text-grey-6 q-ma-none">
              {{ t('composites.TransactionProcessingDashboard.transmissions.showing_results', { count: transmissions.length }) }}
            </p>
          </div>
        </div>
      </template>

      <!-- Status column -->
      <template #body-cell-status="props">
        <q-td :props="props">
          <q-badge
            :color="getStatusColor(props.value)"
            :label="getStatusLabel(props.value)"
            class="q-px-sm" />
        </q-td>
      </template>

      <!-- Type column -->
      <template #body-cell-type="props">
        <q-td :props="props">
          <q-chip
            :color="getTypeColor(props.value)"
            :label="getTypeLabel(props.value)"
            size="sm"
            text-color="white" />
        </q-td>
      </template>

      <!-- Attempts column -->
      <template #body-cell-attempt_count="props">
        <q-td :props="props">
          <div class="row items-center q-gutter-xs">
            <span class="text-weight-medium">{{ props.value }}</span>
            <q-icon
              v-if="props.value > 1"
              :name="symRoundedWarning"
              color="warning"
              size="sm">
              <q-tooltip>{{ t('composites.TransactionProcessingDashboard.transmissions.multiple_attempts') }}</q-tooltip>
            </q-icon>
          </div>
        </q-td>
      </template>

      <!-- Actions column -->
      <template #body-cell-actions="props">
        <q-td :props="props">
          <q-btn-group>
            <q-btn
              v-if="props.row.status === 'failed'"
              :icon="symRoundedReplay"
              color="warning"
              size="sm"
              flat
              @click="$emit('retry', props.row)">
              <q-tooltip>{{ t('composites.TransactionProcessingDashboard.transmissions.retry') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="props.row.error_log?.length"
              :icon="symRoundedError"
              color="negative"
              size="sm"
              flat
              @click="$emit('view-error', props.row)">
              <q-tooltip>{{ t('composites.TransactionProcessingDashboard.transmissions.view_error') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="props.row.status === 'pending'"
              :icon="symRoundedCancel"
              color="grey"
              size="sm"
              flat
              @click="$emit('cancel', props.row)">
              <q-tooltip>{{ t('composites.TransactionProcessingDashboard.transmissions.cancel') }}</q-tooltip>
            </q-btn>
          </q-btn-group>
        </q-td>
      </template>

      <!-- No data message -->
      <template #no-data>
        <div class="full-width row flex-center q-gutter-sm">
          <q-icon
            :name="symRoundedInbox"
            size="2em" />
          <span>{{ t('composites.TransactionProcessingDashboard.transmissions.no_data') }}</span>
        </div>
      </template>
    </q-table>
  </div>
</template>

<script setup lang="ts">
import { symRoundedCancel, symRoundedError, symRoundedInbox, symRoundedReplay, symRoundedWarning } from '@quasar/extras/material-symbols-rounded'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TransmissionStatus, TransmissionTimelineEmits, TransmissionTimelineProps, TransmissionType } from '../types'

const props = withDefaults(defineProps<TransmissionTimelineProps>(), {
  loading: false,
  interactive: true,
})

const emit = defineEmits<TransmissionTimelineEmits>()

const { t } = useI18n()

// Table columns
const columns = computed(() => [
  {
    name: 'id',
    label: t('composites.TransactionProcessingDashboard.transmissions.columns.id'),
    field: 'id',
    sortable: true,
    align: 'left' as const,
    style: 'width: 120px',
  },
  {
    name: 'status',
    label: t('composites.TransactionProcessingDashboard.transmissions.columns.status'),
    field: 'status',
    sortable: true,
    align: 'center' as const,
    style: 'width: 120px',
  },
  {
    name: 'type',
    label: t('composites.TransactionProcessingDashboard.transmissions.columns.type'),
    field: 'type',
    sortable: true,
    align: 'left' as const,
    style: 'width: 180px',
  },
  {
    name: 'attempt_count',
    label: t('composites.TransactionProcessingDashboard.transmissions.columns.attempts'),
    field: 'attempt_count',
    sortable: true,
    align: 'center' as const,
    style: 'width: 100px',
  },
  {
    name: 'created_at',
    label: t('composites.TransactionProcessingDashboard.transmissions.columns.created'),
    field: 'created_at',
    sortable: true,
    align: 'left' as const,
    style: 'width: 150px',
    format: (val: string) => new Date(val).toLocaleString(),
  },
  {
    name: 'transmitted_at',
    label: t('composites.TransactionProcessingDashboard.transmissions.columns.transmitted'),
    field: 'transmitted_at',
    sortable: true,
    align: 'left' as const,
    style: 'width: 150px',
    format: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
  },
  {
    name: 'actions',
    label: t('composites.TransactionProcessingDashboard.transmissions.columns.actions'),
    field: 'actions',
    sortable: false,
    align: 'center' as const,
    style: 'width: 120px',
  },
])

/**
 * Get status color
 */
const getStatusColor = (status: TransmissionStatus): string => {
  switch (status) {
    case 'success':
      return 'positive'
    case 'failed':
      return 'negative'
    case 'pending':
      return 'warning'
    case 'processing':
      return 'info'
    case 'cancelled':
      return 'grey'
    default:
      return 'grey'
  }
}

/**
 * Get status label
 */
const getStatusLabel = (status: TransmissionStatus): string => {
  return t(`composites.TransactionProcessingDashboard.transmission_status.${status}`)
}

/**
 * Get type color
 */
const getTypeColor = (type: TransmissionType): string => {
  switch (type) {
    case 'external_invoice':
      return 'primary'
    case 'accounting_batch':
      return 'secondary'
    case 'external_correction':
      return 'warning'
    case 'internal_correction':
      return 'info'
    case 'external_cancellation':
      return 'negative'
    case 'internal_cancellation':
      return 'grey'
    default:
      return 'grey'
  }
}

/**
 * Get type label
 */
const getTypeLabel = (type: TransmissionType): string => {
  return t(`composites.TransactionProcessingDashboard.transmission_type.${type}`)
}
</script>

<style lang="scss" scoped>
.transmission-table {
  .q-table {
    .q-td {
      white-space: nowrap;
    }
  }
}
</style>
